import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import * as complianceService from '../services/complianceService';
import PageHeader from './Navigation/PageHeader';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, LineChart, Line, Area, AreaChart
} from 'recharts';
import usePersistedTab from '../hooks/usePersistedTab';

const { FiArrowLeft, FiShield, FiCheck, FiAlertTriangle, FiX, FiRefreshCw, FiFileText, FiScale, FiUsers, FiTrendingUp } = FiIcons;

const ComplianceTracker = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overallScore, setOverallScore] = useState(null);
  const [complianceAreas, setComplianceAreas] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = usePersistedTab('employment-contracts');

  useEffect(() => {
    loadComplianceData();
  }, []);

  const loadComplianceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [scoreData, areasData] = await Promise.all([
        complianceService.getOverallComplianceScore(),
        complianceService.getComplianceByArea()
      ]);

      setOverallScore(scoreData);
      setComplianceAreas(areasData);
    } catch (err) {
      console.error('Error loading compliance data:', err);
      setError('Failed to load compliance data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadComplianceData();
    setRefreshing(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant': return FiCheck;
      case 'at-risk': return FiAlertTriangle;
      case 'non-compliant': return FiX;
      default: return FiAlertTriangle;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant': return 'text-green-600 bg-green-50 border-green-200';
      case 'at-risk': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'non-compliant': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getAreaStatusColor = (status) => {
    switch (status) {
      case 'compliant': return 'bg-green-600';
      case 'at-risk': return 'bg-yellow-600';
      case 'non-compliant': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <PageHeader
          title="Compliance Tracker"
          subtitle="Loading compliance data..."
          icon={FiShield}
        />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <PageHeader
          title="Compliance Tracker"
          subtitle="Error loading compliance data"
          icon={FiShield}
        />
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <SafeIcon icon={FiX} className="text-red-600 text-4xl mx-auto mb-4" />
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={loadComplianceData}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const overallPercentage = overallScore?.overall_score || 0;
  const atRiskCount = overallScore?.cases_at_risk || 0;
  const nonCompliantCount = overallScore?.non_compliant_cases || 0;

  // Get area data for active tab
  const getAreaData = (areaName) => {
    return complianceAreas.find(area => area.area === areaName) || {};
  };

  // Tab configuration
  const tabs = [
    { id: 'employment-contracts', name: 'Employment Contracts', icon: FiFileText, areaName: 'Employment Contracts' },
    { id: 'disciplinary', name: 'Disciplinary Procedures', icon: FiScale, areaName: 'Disciplinary Procedures' },
    { id: 'legal-actions', name: 'Legal Critical Actions', icon: FiAlertTriangle, areaName: 'Legal Critical Actions' },
    { id: 'workflows', name: 'Company Workflows', icon: FiTrendingUp, areaName: 'Company Workflows' }
  ];

  const currentArea = getAreaData(tabs.find(t => t.id === activeTab)?.areaName);

  // Prepare chart data
  const complianceChartData = [
    { name: 'Compliant', value: currentArea.compliant_items || 0, color: '#10b981' },
    { name: 'At Risk', value: currentArea.at_risk_items || 0, color: '#f59e0b' },
    { name: 'Non-Compliant', value: currentArea.non_compliant_items || 0, color: '#ef4444' }
  ];

  const barChartData = complianceAreas.map(area => ({
    name: area.area?.split(' ')[0] || 'Unknown',
    compliance: Math.round(area.compliance_rate || 0),
    atRisk: area.at_risk_items || 0,
    nonCompliant: area.non_compliant_items || 0
  }));

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link to="/" className="mr-4">
            <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Compliance Tracker</h1>
            <p className="text-gray-600">Real-time legal compliance monitoring across all HR processes</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <SafeIcon icon={FiRefreshCw} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Summary Cards - Always Visible */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
        <div className={`border rounded-lg p-6 text-center ${overallPercentage >= 90 ? 'bg-green-50 border-green-200' :
          overallPercentage >= 75 ? 'bg-yellow-50 border-yellow-200' :
            'bg-red-50 border-red-200'
          }`}>
          <SafeIcon icon={FiCheck} className={`text-3xl mx-auto mb-2 ${overallPercentage >= 90 ? 'text-green-600' :
            overallPercentage >= 75 ? 'text-yellow-600' :
              'text-red-600'
            }`} />
          <h3 className={`text-2xl font-bold ${overallPercentage >= 90 ? 'text-green-900' :
            overallPercentage >= 75 ? 'text-yellow-900' :
              'text-red-900'
            }`}>{Math.round(overallPercentage)}%</h3>
          <p className={overallPercentage >= 90 ? 'text-green-700' : overallPercentage >= 75 ? 'text-yellow-700' : 'text-red-700'}>
            Overall Compliance
          </p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <SafeIcon icon={FiAlertTriangle} className="text-yellow-600 text-3xl mx-auto mb-2" />
          <h3 className="text-2xl font-bold text-yellow-900">{atRiskCount}</h3>
          <p className="text-yellow-700">Cases At Risk</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <SafeIcon icon={FiX} className="text-red-600 text-3xl mx-auto mb-2" />
          <h3 className="text-2xl font-bold text-red-900">{nonCompliantCount}</h3>
          <p className="text-red-700">Non-Compliant Cases</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="border-b border-gray-200">
          {/* Mobile Dropdown */}
          <div className="md:hidden p-4">
            <label htmlFor="compliance-tabs" className="sr-only">Select a section</label>
            <select
              id="compliance-tabs"
              className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 py-2 px-3"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>{tab.name}</option>
              ))}
            </select>
          </div>

          {/* Desktop Tabs */}
          <nav className="hidden md:flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <SafeIcon icon={tab.icon} className="text-lg" />
                  <span className="hidden md:inline">{tab.name}</span>
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Area Header */}
          <div className={`${getAreaStatusColor(currentArea.status)} px-6 py-4 rounded-lg mb-6`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiShield} className="text-white text-2xl" />
                <h3 className="text-white font-semibold text-xl">{currentArea.area}</h3>
              </div>
              <div className="flex items-center space-x-3">
                <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded text-sm font-medium">
                  {Math.round(currentArea.compliance_rate || 0)}% Compliant
                </span>
                <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded text-sm font-medium capitalize">
                  {(currentArea.status || 'unknown').replace('-', ' ')}
                </span>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Pie Chart - Compliance Distribution */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Compliance Distribution</h4>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={complianceChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    style={{ fontSize: '14px' }}
                  >
                    {complianceChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '14px' }} />
                  <Legend wrapperStyle={{ fontSize: '14px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Bar Chart - All Areas Comparison */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Cross-Area Comparison</h4>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" style={{ fontSize: '12px' }} />
                  <YAxis style={{ fontSize: '12px' }} />
                  <Tooltip contentStyle={{ fontSize: '14px' }} />
                  <Legend wrapperStyle={{ fontSize: '14px' }} />
                  <Bar dataKey="compliance" fill="#10b981" name="Compliance %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-gray-900">{currentArea.total_items || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Total Items</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{currentArea.compliant_items || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Compliant</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-yellow-600">{currentArea.at_risk_items || 0}</p>
              <p className="text-sm text-gray-600 mt-1">At Risk</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{currentArea.non_compliant_items || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Non-Compliant</p>
            </div>
          </div>

          {/* Additional Details */}
          {currentArea.details && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Additional Details</h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
                {Object.entries(currentArea.details).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="capitalize font-medium">{key.replace(/_/g, ' ')}:</span>
                    <span className="font-bold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceTracker;